/*    
<summary>
  This functional component "ClientFilterForm" provides form to get data based on filter
  Developer:Aashish Singh, Created Date:20-March-2024
</summary>
<returns>Returns JSX</returns>
*/
import { Field, Form, Formik, useFormikContext } from "formik";
import moment from "moment";
import React, { useState } from "react";
import { Button, Col, Modal, Row } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { IOption } from "../../../models/ICommon";
import FormikFormInput from "../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormSelectField from "../../../shared-components/FormikFormHandlers/FormikFormSelectField";
import Label from "../../../shared-components/label/label";
import SpinnerLoader from "../../../shared-components/loader/SpinnerLoader";
import FormikFormTimePicker from "../../../shared-components/FormikFormHandlers/FormikFormTimePicker";
import LoaderButton from "../../../shared-components/button/loader-button";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import FormikFormDatePicker from "../../../shared-components/FormikFormHandlers/FormikFormDatePicker";
import FormikDateFieldMUI from "../../../shared-components/FormikFormHandlers/FormikDateFieldMUI";
import { PublishScheduleValidationSchema } from "../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import dayjs from "dayjs";
import { DATE_FORMAT } from "../../../constants/constants";
import { useStore } from "../../../contexts/store-provider";

interface IProps {
  submitHandler: (values: any) => void;
  onModalClosed: () => void;
  actionInProgress: boolean;
  initialValues: any;
  storeId: number;
  selectedOption: number;
  publishDateRange: {
    startDate: Date | null;
    endDate: Date | null;
  };
  dateSchedules: Date[];
}

const PublishForm = (props: IProps) => {
  const {
    submitHandler,
    onModalClosed,
    initialValues,
    actionInProgress,
    publishDateRange,
    dateSchedules,
  } = props;

  const { shiftManagementStore, masterManagementStore } = useStore();
  const { getClosedDays } = shiftManagementStore;
  const { getReservationSettingDetail } = masterManagementStore;
  const FormObserver: React.FC = () => {
    const { values } = useFormikContext<any>();
    return null;
  };

  return (
    <>
      <Modal className="add-user-modal filter-from " show={true} centered>
        <Modal.Header>
          <Modal.Title>
            <FormattedMessage
              id={
                props.selectedOption === 1
                  ? `publish_schedule`
                  : `unpublish_schedule`
              }
            />
          </Modal.Title>
          <div onClick={onModalClosed} className="Crossicons">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="23"
              height="23"
              fill="currentColor"
              className="bi bi-x"
              viewBox="0 0 16 16"
            >
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
            </svg>
          </div>
        </Modal.Header>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={PublishScheduleValidationSchema(
            publishDateRange,
            props.selectedOption,
            getClosedDays ? getClosedDays.map((day: any) => day.WeekDays) : [],
            getReservationSettingDetail
          )}
          onSubmit={async (values: any) => {
            submitHandler(values);
          }}
        >
          {({ values }) => (
            <Form className="user-form" noValidate>
              {actionInProgress && <FormLoader loading={actionInProgress} />}
              <FormObserver />
              <Modal.Body>
                <Row className="date-range">
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="start_date"
                      />
                      <Field
                        name="StartDate"
                        as={FormikDateFieldMUI}
                        min={dayjs().format(DATE_FORMAT)}
                        disableFuture={false}
                        publishDateRange={publishDateRange}
                        isScheduleCheck={
                          props.selectedOption === 1 ? true : false
                        }
                        isWeekdayScheduleCheck={
                          props.selectedOption === 2 ? true : false
                        }
                        getClosedDays={getClosedDays}
                        reservationSettingDetail={getReservationSettingDetail}
                      />
                    </div>
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="end_date"
                      />
                      <Field
                        name="EndDate"
                        as={FormikDateFieldMUI}
                        min={dayjs().format(DATE_FORMAT)}
                        disableFuture={false}
                        publishDateRange={publishDateRange}
                        isScheduleCheck={
                          props.selectedOption === 1 ? true : false
                        }
                        isWeekdayScheduleCheck={
                          props.selectedOption === 2 ? true : false
                        }
                        getClosedDays={getClosedDays}
                        reservationSettingDetail={getReservationSettingDetail}
                      />
                    </div>
                  </div>
                </Row>

                {/* {
                  err &&
                  <div className="error-text text-center w-100">
                  <span data-testid="testError" className="text-danger errorMessage ">
                    <FormattedMessage id="please_select_at_least_1_field" />
                  </span>
                  </div>
                } */}
              </Modal.Body>
              <div className="modal-footer border-0">
                <div className="m-auto">
                  <LoaderButton
                    type="submit"
                    isLoading={actionInProgress}
                    disabled={actionInProgress}
                    text={props.selectedOption === 1 ? `publish` : `unpublish`}
                  />
                  <Button variant="outline-danger" className="ms-2" onClick={onModalClosed}>
                    <FormattedMessage id="button_cancel" />
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export default PublishForm;
